import React from 'react';
import {
  isUndefined,
  findIndex,
  differenceWith,
  cloneDeep,
  isEmpty,
  sortBy,
  some,
  join,
} from 'lodash';
import { filter, pluck, flow } from 'lodash/fp';
import { CSSTransition } from 'react-transition-group';
import { ButtonGroup, Button } from 'ui';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import CheckboxGroup from 'components/shared/CheckboxGroup';
import CheckboxField from 'components/shared/CheckboxGroup/CheckboxField';
import Loadable from 'components/shared/loadable';
import RadioFieldGroup from 'components/shared/RadioFieldGroup';
import RadioField from 'components/shared/RadioFieldGroup/RadioField';
import LinkButton from 'components/shared/LinkButton';
import AlertBox, { Heading, SubHeading } from 'components/shared/AlertBox';
import SettingsHeading from 'components/settings/SettingsHeading';
import InfoTooltip from 'components/shared/InfoTooltip';
import FeatureFlag from 'components/shared/FeatureFlag';
import { hasFeatureFlags } from 'utils/features';
import ConfirmationModal from './modals/ConfirmationModal';
import EventManagement from './sections/EventManagement';
import RelatedUrlLabel from './shared/RelatedUrlLabel';
import ContentLibraryLabel from './shared/ContentLibraryLabel';
import SkillsManagement from './sections/SkillsManagement';

const ADMIN = 'admin';
const CUSTOM = 'custom';
const maxRoleNameLength = 25;
const roleNameCharactersLeftThreshold = 5;

const ErrorMessage = ({ errors, onCancel }) => (
  <AlertBox onDismiss={onCancel}>
    <Heading>{t('common.error')}</Heading>
    {errors.map(error => (
      <SubHeading key={error}>{error}</SubHeading>
    ))}
  </AlertBox>
);

const DeleteModal = ({ onDismiss, onSubmit }) => (
  <AlertBox onDismiss={onDismiss}>
    <Heading>{t('settings.tab.roles.delete.delete_role')}</Heading>
    <SubHeading>
      {t('settings.tab.roles.delete.confirmation_message')}
    </SubHeading>
    <ButtonGroup alignEnd>
      <LinkButton
        type="primary"
        className="cancel mr-24 ml-24"
        onClick={onDismiss}
      >
        {t('common.cancel')}
      </LinkButton>
      <Button onClick={onSubmit} primary>
        {t('settings.tab.roles.delete.confirm_button_text')}
      </Button>
    </ButtonGroup>
  </AlertBox>
);

class EditRole extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      permissionsToGrant: new Set(),
      permissionsToRevoke: new Set(),
      showDeleteModal: false,
      roleName: props.role.name || '',
      startingRoles: [],
      roles: [],
      currentRoleId: props.role.id,
      errors: [],
      loading: !!props.role.id,
      starting: {},
      deletePeopleManagerships: false,
      deleteContentOwnerships: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.updateRoleName = this.updateRoleName.bind(this);
  }

  componentDidMount() {
    if (this.state.currentRoleId) {
      request
        .get(routes.ROLE_PERMISSIONS(this.state.currentRoleId))
        .then(payload => {
          const mappedPermissions = payload.permissions.reduce(
            (newObject, currentPermission) => {
              newObject[currentPermission] = true;
              return newObject;
            },
            {},
          );

          this.setState({
            ...mappedPermissions,
            loading: false,
            starting: { ...mappedPermissions },
          });
        });
    }

    // Editing
    if (this.state.currentRoleId) {
      request
        .get(routes.MANAGED_ROLES.INDEX(this.state.currentRoleId))
        .then(response => {
          this.setState({
            roles: response,
            startingRoles: cloneDeep(response),
          });
        });

      // New
    } else {
      request.get(routes.ROLES).then(response => {
        const roles = response.map(role => {
          return { id: role.id, name: role.name, canManage: false };
        });

        this.setState({
          roles,
          startingRoles: cloneDeep(roles),
        });
      });
    }

    window.scrollTo(0, 0);
  }

  pageHeading() {
    if (this.state.currentRoleId) {
      if (this.props.role.editable) {
        return t('settings.tab.roles.edit.title');
      }
      return t('settings.tab.roles.view.title');
    }
    return t('settings.tab.roles.new.title');
  }

  deletable() {
    return (
      this.state.currentRoleId &&
      this.props.role.default_role_mapping === CUSTOM
    );
  }

  nameEditable() {
    return this.props.role.default_role_mapping === CUSTOM;
  }

  nameBlank() {
    return !this.state.roleName || this.state.roleName.match(/^\s*$/);
  }

  nameChanged() {
    return this.props.role.name !== this.state.roleName;
  }

  roleNameCharactersLeft = () => {
    return maxRoleNameLength - this.state.roleName.length;
  };

  showCharactersRemaining = () => {
    return this.roleNameCharactersLeft() <= roleNameCharactersLeftThreshold;
  };

  updateRoleName(e) {
    this.setState({ roleName: e.target.value.slice(0, maxRoleNameLength) });
  }

  handleCheckboxChange(e) {
    e.stopPropagation();
    const { name, checked } = e.target;

    if (checked) {
      this.permissionGranted(name);
      if (name === 'publish_content') this.permissionGranted('create_content');
    } else {
      if (name === 'edit_all')
        this.permissionRevoked('chrome_extension_mappings');
      if (name === 'publish_content') this.permissionRevoked('create_content');
      this.permissionRevoked(name);
    }
  }

  handlePeopleManagementRadioChange(e) {
    const { value } = e.target;

    if (value === 'manage_groups') {
      this.permissionGranted(value);
      this.permissionRevoked('edit_all');
      this.permissionRevoked('manage_all');
      this.permissionRevoked('chrome_extension_mappings');
    }

    if (value === 'manage_all') {
      this.permissionGranted(value);
      this.permissionRevoked('manage_groups');
      this.permissionRevoked('edit_groups');
    }

    if (value === 'no_access') {
      this.permissionRevoked('edit_all');
      this.permissionRevoked('manage_all');
      this.permissionRevoked('manage_groups');
      this.permissionRevoked('edit_groups');
      this.permissionRevoked('chrome_extension_mappings');
    }
  }

  handlePeopleManagement() {
    if (this.state.manage_all) {
      return 'manage_all';
    }

    if (this.state.manage_groups) {
      return 'manage_groups';
    }

    return 'no_access';
  }

  handleContentOwnershipRadioChange(e) {
    const { value } = e.target;

    if (value === 'own_content') {
      this.permissionGranted(value);
      this.permissionRevoked('own_all');
      this.permissionRevoked('create_content');
      this.permissionRevoked('chrome_extension_mappings');
      this.permissionRevoked('publish_content');
    }

    if (value === 'own_all') {
      this.permissionGranted(value);
      this.permissionGranted('create_content');
      this.permissionRevoked('own_content');
      this.permissionGranted('publish_content');
    }

    if (value === 'no_access') {
      this.permissionRevoked('own_all');
      this.permissionRevoked('own_content');
      this.permissionRevoked('create_content');
      this.permissionRevoked('chrome_extension_mappings');
      this.permissionRevoked('publish_content');
    }
  }

  handleCreateContentChange(e) {
    const { checked } = e.target;

    if (!checked) {
      this.permissionRevoked('chrome_extension_mappings');
    }

    this.handleCheckboxChange(e);
  }

  handlePublishContentChange(e) {
    const { checked } = e.target;

    if (!checked) {
      this.permissionRevoked('publish_content');
    }

    this.handleCheckboxChange(e);
  }

  handleManagedRoleChange = e => {
    const value = Number(e.target.value);
    const { roles } = this.state;
    const roleIndex = findIndex(this.state.roles, { id: value });
    roles[roleIndex].canManage = !roles[roleIndex].canManage;

    this.setState({ roles });
  };

  handleContentOwnership() {
    if (this.state.own_all) return 'own_all';
    if (this.state.own_content) return 'own_content';
    return 'no_access';
  }

  setPermissionsForConfirmationModal() {
    this.setState(state => ({
      deletePeopleManagerships:
        !isUndefined(state.starting.manage_groups) &&
        state.starting.manage_groups !== state.manage_groups,
      deleteContentOwnerships:
        !isUndefined(state.starting.own_content) &&
        state.starting.own_content !== state.own_content,
    }));
  }

  showConfirmationModal() {
    return (
      this.state.deletePeopleManagerships || this.state.deleteContentOwnerships
    );
  }

  eventManagementState() {
    if (this.state.edit_all_events) return 'edit_all_events';
    if (this.state.create_events) return 'create_events';
    if (this.state.instruct_events) return 'instruct_events';
    return 'no_access';
  }

  handleEventManagementRadioChange(e) {
    const { value } = e.target;

    if (value === 'edit_all_events') {
      this.permissionRevoked('create_events');
      this.permissionGranted('instruct_events');
      this.permissionGranted(value);
    }

    if (value === 'create_events') {
      this.permissionRevoked('edit_all_events');
      this.permissionGranted('instruct_events');
      this.permissionGranted(value);
    }

    if (value === 'instruct_events') {
      this.permissionRevoked('edit_all_events');
      this.permissionRevoked('create_events');
      this.permissionGranted(value);
    }

    if (value === 'no_access') {
      this.permissionRevoked('edit_all_events');
      this.permissionRevoked('create_events');
      this.permissionRevoked('instruct_events');
    }
  }

  skillsManagementState() {
    if (this.state.skills_admin) return 'skills_admin';
    return 'no_access';
  }

  handleSkillsManagementRadioChange(e) {
    const { value } = e.target;

    if (value === 'no_access') {
      this.permissionRevoked('skills_admin');
    }

    if (value === 'skills_admin') {
      this.permissionGranted('skills_admin');
    }
  }

  permissionGranted(name) {
    const { permissionsToGrant, permissionsToRevoke, starting } = this.state;

    if (permissionsToRevoke.has(name)) {
      permissionsToRevoke.delete(name);
    }
    if (!starting[name]) {
      permissionsToGrant.add(name);
    }

    this.setState(
      {
        permissionsToGrant,
        permissionsToRevoke,
        [name]: true,
      },
      () => {
        this.setPermissionsForConfirmationModal();
      },
    );
  }

  permissionRevoked(name) {
    const { permissionsToGrant, permissionsToRevoke, starting } = this.state;

    if (permissionsToGrant.has(name)) {
      permissionsToGrant.delete(name);
    }
    if (starting[name]) {
      permissionsToRevoke.add(name);
    }

    this.setState(
      {
        permissionsToGrant,
        permissionsToRevoke,
        [name]: false,
      },
      () => {
        this.setPermissionsForConfirmationModal();
      },
    );
  }

  handleSubmit() {
    if (
      (this.state.edit_groups || this.state.edit_all) &&
      !some(this.state.roles, { canManage: true })
    ) {
      this.setState({
        errors: [
          t(
            'settings.tab.roles.edit.people_management_managed_roles.no_selection_message',
          ),
        ],
      });
      return;
    }

    if (!this.state.currentRoleId) {
      this.createRole();
    } else if (this.showConfirmationModal()) {
      this.setState({ showConfirmationModal: true });
    } else {
      this.updateRole();
    }
  }

  updateManagedRoles = () => {
    const roleDifference = differenceWith(
      this.state.roles,
      this.state.startingRoles,
      (a, b) => a.canManage === b.canManage && a.id === b.id,
    );

    const getIdsFor = canManageState => {
      return flow(filter({ canManage: canManageState }), pluck('id'));
    };
    const roleIdsToGrant = getIdsFor(true)(roleDifference);
    const roleIdsToRevoke = getIdsFor(false)(roleDifference);

    if (!isEmpty(roleIdsToGrant)) {
      request.post(routes.MANAGED_ROLES.CREATE(this.state.currentRoleId), {
        role_ids: roleIdsToGrant,
      });
    }

    if (!isEmpty(roleIdsToRevoke)) {
      request.delete(routes.MANAGED_ROLES.DELETE(this.state.currentRoleId), {
        role_ids: roleIdsToRevoke,
      });
    }
  };

  createRole() {
    request
      .post(routes.ROLES, { role: { name: this.state.roleName } })
      .then(rolePayload => {
        this.setState(
          { currentRoleId: rolePayload.id },
          this.updatePermissions,
        );
      })
      .fail(failPayload => {
        this.setState({ errors: failPayload.responseJSON.errors });
      });
  }

  updateRole() {
    if (!this.nameChanged()) {
      this.updatePermissions();
    } else {
      request
        .put(routes.ROLE(this.state.currentRoleId), {
          role: { name: this.state.roleName },
        })
        .then(rolePayload => {
          this.updatePermissions();
        })
        .fail(failPayload => {
          this.setState({ errors: failPayload.responseJSON.errors });
        });
    }
  }

  deleteRole(role) {
    request
      .delete(routes.ROLE(role.id))
      .then(() => {
        this.setState({ showDeleteModal: false });
        this.props.onCancel();
      })
      .fail(failPayload => {
        this.setState({ showDeleteModal: false });
        this.setState({ errors: failPayload.responseJSON.errors });
      });
  }

  updatePermissions() {
    const requests = [];

    if (this.state.permissionsToRevoke.has('own_content')) {
      if (this.state.permissionsToGrant.has('own_all')) {
        // delete ownerships except expert
        requests.push(
          request.delete(
            routes.ROLE_OWNERSHIPS(this.state.currentRoleId) +
              '?exclude_experts=t',
          ),
        );
      } else {
        // delete all ownerships
        requests.push(
          request.delete(routes.ROLE_OWNERSHIPS(this.state.currentRoleId)),
        );
      }
    }

    if (
      this.state.permissionsToRevoke.has('own_all') &&
      !this.state.permissionsToGrant.has('own_content')
    ) {
      requests.push(
        request.delete(routes.ROLE_OWNERSHIPS(this.state.currentRoleId)),
      );
    }

    this.state.permissionsToGrant.forEach(p =>
      requests.push(
        request.post(routes.PERMISSION(this.state.currentRoleId, p)),
      ),
    );
    this.state.permissionsToRevoke.forEach(p =>
      requests.push(
        request.delete(routes.PERMISSION(this.state.currentRoleId, p)),
      ),
    );
    Promise.all(requests)
      .then(() => this.props.onCancel())
      .catch(() => this.props.onCancel());

    this.updateManagedRoles();
  }

  deleteButtonDisabled() {
    return this.props.role.has_users || this.props.role.has_smart_group_rule;
  }

  deleteButtonTooltipText() {
    if (this.deleteButtonDisabled()) {
      const introText = t(
        'settings.tab.roles.delete.disabled_explanation.intro',
      );
      const reasons = [];

      if (this.props.role.has_users) {
        reasons.push(
          t('settings.tab.roles.delete.disabled_explanation.has_users'),
        );
      }
      if (this.props.role.has_smart_group_rule) {
        reasons.push(
          t(
            'settings.tab.roles.delete.disabled_explanation.has_smart_group_rule',
          ),
        );
      }

      return `${introText} ${join(reasons, ' and ')}`;
    }
    return null;
  }

  submitButtonText() {
    if (this.props.role.id) {
      if (this.props.role.editable) {
        return t('settings.tab.roles.edit.save_role');
      }
      return t('common.done');
    }
    return t('settings.tab.roles.new.create');
  }

  contentTypesText() {
    const { hasPractice, hasOnDemandPractice } = this.props.features;
    const hasPracticeAndODP = hasPractice && hasOnDemandPractice;

    if (hasPracticeAndODP)
      return t(
        'settings.tab.roles.edit.content_text_with_practice_and_certifications',
      );

    if (hasPracticeAndODP)
      return t('settings.tab.roles.edit.content_text_with_practice');

    if (!hasPracticeAndODP)
      return t('settings.tab.roles.edit.content_text_with_certifications');

    return t('settings.tab.roles.edit.content_text');
  }

  render() {
    const {
      hasChromeExtension,
      hasWebWidget,
      hasPractice,
      hasOnDemandPractice,
    } = this.props.features;

    const hasUnificationFeatureFlag = hasFeatureFlags([
      'readiness-enable-unification',
    ]);

    const PermissionCheckbox = ({ disabled, ...props }) => (
      <CheckboxField
        className="EditRole-checkbox"
        onChange={e => {
          this.handleCheckboxChange(e);
        }}
        disabled={!this.props.role.editable || disabled}
        {...props}
      />
    );

    const hasPracticeAndODP = hasPractice && hasOnDemandPractice;

    return (
      <Loadable loading={this.state.loading}>
        <div className="EditRole">
          {this.state.errors && this.state.errors.length > 0 && (
            <ErrorMessage
              errors={this.state.errors}
              onCancel={() => {
                this.setState({ errors: [] });
              }}
            />
          )}
          {this.state.showDeleteModal && (
            <DeleteModal
              onDismiss={() => this.setState({ showDeleteModal: false })}
              onSubmit={() => this.deleteRole(this.props.role)}
            />
          )}
          {this.state.showConfirmationModal && (
            <ConfirmationModal
              onDismiss={() => this.setState({ showConfirmationModal: false })}
              onSubmit={() => this.updateRole()}
              roleName={this.state.roleName}
              permissionsToGrant={this.state.permissionsToGrant}
              deleteContentOwnerships={this.state.deleteContentOwnerships}
              deletePeopleManagerships={this.state.deletePeopleManagerships}
            />
          )}
          <SettingsHeading>{this.pageHeading()}</SettingsHeading>
          <div className="EditRole-roleNameSection">
            <label className="EditRole-roleNameLabel" htmlFor="role_name">
              {t('settings.tab.roles.edit.role_name_label')}
            </label>
            <input
              name="roleName"
              type="text"
              aria-label={t('settings.tab.roles.edit.role_name_label')}
              disabled={!this.nameEditable() || this.state.loading}
              defaultValue={this.props.role.name}
              onChange={this.updateRoleName}
              maxLength={maxRoleNameLength}
            />
            {this.state.currentRoleId && (
              <span className="ml-16 c-brand-gray-darkest">
                {t('settings.tab.roles.edit.role_id_label')}:{' '}
                {this.state.currentRoleId}
              </span>
            )}
            {this.showCharactersRemaining() && (
              <div className="fz-12 c-gray-dark mt-4">
                {t('settings.tab.roles.edit.role_name_characters_left', {
                  count: this.roleNameCharactersLeft(),
                })}
              </div>
            )}
          </div>
          <div className="EditRole-section">
            <h3>{t('settings.tab.roles.edit.learning.title')}</h3>

            <ul className="ml-16">
              {!hasPracticeAndODP && (
                <li>
                  {t('settings.tab.roles.edit.learning_permission_label')}
                </li>
              )}
              {hasPracticeAndODP && (
                <>
                  <li>
                    {t(
                      'settings.tab.roles.edit.learning_permission_label_assignments',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.tab.roles.edit.learning_permission_label_completion_include_practice_scenarios',
                    )}
                  </li>
                </>
              )}
              <FeatureFlag features="skills">
                <li>
                  {t(
                    'settings.tab.roles.edit.learning_permission_label_skills_completion',
                  )}
                </li>
              </FeatureFlag>
            </ul>
          </div>

          <div className="EditRole-segment">
            {this.props.role.editable
              ? t('settings.tab.roles.edit.customizable_permissions_label')
              : t('settings.tab.roles.edit.read_only_permissions_label')}
          </div>

          <div className="EditRole-section">
            <h3>{t('settings.tab.roles.edit.people_management.title')}</h3>
            <div className="EditRole-sectionDescription">
              {t('settings.tab.roles.edit.people_management.desc')}
            </div>
            <RadioFieldGroup
              name="peopleManagement"
              disabled={!this.props.role.editable || this.state.loading}
              selectedValue={this.handlePeopleManagement()}
              onChange={e => {
                this.handlePeopleManagementRadioChange(e);
              }}
            >
              <RadioField value="no_access">
                {t(
                  'settings.tab.roles.edit.people_management_labels.no_access',
                )}
              </RadioField>

              <RadioField value="manage_groups">
                {t(
                  'settings.tab.roles.edit.people_management_labels.group_by_group_access',
                )}
              </RadioField>
              <div className="EditRole-secondaryOptions">
                <ul>
                  <li>
                    {t(
                      'settings.tab.roles.edit.people_management_view_activity_label',
                    )}
                  </li>
                  <li>
                    {t(
                      'settings.tab.roles.edit.people_management_deploy_content_label',
                    )}
                  </li>
                  <li>
                    {t('settings.tab.roles.edit.people_management_grade_label')}
                  </li>
                  <FeatureFlag
                    features={['readiness-enable-lesson-mark-as-complete']}
                  >
                    <li data-testid="mark-as-complete-manage-groups">
                      {t(
                        'settings.tab.roles.edit.mark_as_complete.manage_groups',
                      )}
                    </li>
                  </FeatureFlag>
                  <FeatureFlag features="skills">
                    <li>
                      {t(
                        'settings.tab.roles.edit.people_management_skills_complete_assessment_label',
                      )}
                    </li>
                    <li>
                      {t(
                        'settings.tab.roles.edit.people_management_skills_request_assessment_and_coaching_plan_label',
                      )}
                    </li>
                    <li>
                      {t(
                        'settings.tab.roles.edit.people_management_skills_export_report_label',
                      )}
                    </li>
                  </FeatureFlag>
                </ul>

                {!hasUnificationFeatureFlag && (
                  <PermissionCheckbox
                    checked={this.state.edit_groups && !this.state.edit_all}
                    disabled={!this.state.manage_groups}
                    name="edit_groups"
                  >
                    {t(
                      'settings.tab.roles.edit.people_management_create_managed_label',
                    )}
                  </PermissionCheckbox>
                )}
              </div>

              <RadioField value="manage_all">
                {t(
                  'settings.tab.roles.edit.people_management_labels.full_access',
                )}
              </RadioField>

              <div className="EditRole-secondaryOptions">
                <ul>
                  <li>
                    {t('settings.tab.roles.edit.global_view_activity_label')}
                  </li>
                  <li>
                    {t('settings.tab.roles.edit.global_deploy_content_label')}
                  </li>
                  <FeatureFlag
                    features={['readiness-enable-lesson-mark-as-complete']}
                  >
                    <li data-testid="mark-as-complete-all-groups">
                      {t('settings.tab.roles.edit.mark_as_complete.all_groups')}
                    </li>
                  </FeatureFlag>
                  <li>{t('settings.tab.roles.edit.global_grading_label')}</li>
                  <li>{t('settings.tab.roles.edit.export_reports_label')}</li>
                  <FeatureFlag features="skills">
                    <li>
                      {t(
                        'settings.tab.roles.edit.people_management_full_access_skills_complete_assessment_label',
                      )}
                    </li>
                    <li>
                      {t(
                        'settings.tab.roles.edit.people_management_full_access_skills_request_assessment_and_coaching_plan_label',
                      )}
                    </li>
                    <li>
                      {t(
                        'settings.tab.roles.edit.people_management_full_access_skills_export_report_label',
                      )}
                    </li>
                  </FeatureFlag>
                </ul>

                {!hasUnificationFeatureFlag && (
                  <>
                    <PermissionCheckbox
                      checked={this.state.edit_all}
                      disabled={!this.state.manage_all}
                      name="edit_all"
                    >
                      {t(
                        'settings.tab.roles.edit.people_management_create_all_label',
                      )}
                    </PermissionCheckbox>

                    <div className="EditRole-secondaryOptions">
                      <ul>
                        <li className={this.state.edit_all ? '' : 'opacity-30'}>
                          {t(
                            'settings.tab.roles.edit.global_use_bulk_user_importer',
                          )}
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </RadioFieldGroup>

            <CSSTransition
              classNames="CSSTransition fade"
              timeout={{ enter: 500, exit: 300 }}
              in={this.state.edit_groups || this.state.edit_all}
              unmountOnExit
            >
              <div className="EditRole-section">
                <div className="EditRole-sectionDescription">
                  <div>
                    <strong>
                      {t(
                        'settings.tab.roles.edit.people_management_managed_roles.title',
                      )}
                    </strong>
                  </div>
                  <div>
                    <small>
                      {t(
                        'settings.tab.roles.edit.people_management_managed_roles.sub_title',
                      )}
                    </small>
                  </div>
                </div>

                {!this.state.loading && (
                  <CheckboxGroup
                    name="managedRoles"
                    onChange={e => this.handleManagedRoleChange(e)}
                    disabled={!this.props.role.editable}
                  >
                    {sortBy(this.state.roles, 'name').map(role => (
                      <CheckboxField
                        key={role.id}
                        value={role.id}
                        className="EditRole-checkbox"
                        checked={role.canManage}
                      >
                        {role.name}
                      </CheckboxField>
                    ))}
                    <span className="c-brand-red">
                      {t(
                        'settings.tab.roles.edit.people_management_managed_roles.validation',
                      )}
                    </span>
                  </CheckboxGroup>
                )}
              </div>
            </CSSTransition>
          </div>

          <div className="EditRole-section">
            <h3>{t('settings.tab.roles.edit.content_ownership.title')}</h3>
            <div className="EditRole-sectionDescription">
              {t('settings.tab.roles.edit.content_ownership.desc', {
                contentTypes: this.contentTypesText(),
              })}
            </div>

            <RadioFieldGroup
              name="contentOwnership"
              disabled={!this.props.role.editable || this.state.loading}
              selectedValue={this.handleContentOwnership()}
              onChange={e => {
                this.handleContentOwnershipRadioChange(e);
              }}
            >
              <RadioField value="no_access">
                {t(
                  'settings.tab.roles.edit.content_ownership_labels.no_access',
                )}
              </RadioField>

              <RadioField value="own_content">
                {t(
                  'settings.tab.roles.edit.content_ownership_labels.item_by_item_access',
                )}
              </RadioField>
              <div className="EditRole-secondaryOptions">
                <PermissionCheckbox
                  checked={
                    !this.state.own_all &&
                    (this.state.create_content || this.state.publish_content)
                  }
                  disabled={
                    !this.state.own_content ||
                    (this.state.own_content && this.state.publish_content)
                  }
                  name="create_content"
                  onChange={e => {
                    this.handleCreateContentChange(e);
                  }}
                  value="on"
                >
                  {t('settings.tab.roles.edit.create_content_label', {
                    contentTypes: this.contentTypesText(),
                  })}
                </PermissionCheckbox>
                <PermissionCheckbox
                  checked={!this.state.own_all && this.state.publish_content}
                  disabled={!this.state.own_content}
                  name="publish_content"
                  onChange={e => {
                    this.handlePublishContentChange(e);
                  }}
                  value="on"
                >
                  {t('settings.tab.roles.edit.publish_content_label')}
                </PermissionCheckbox>
              </div>

              <RadioField value="own_all">
                {t(
                  'settings.tab.roles.edit.content_ownership_labels.full_access',
                )}
              </RadioField>
              <div className="EditRole-secondaryOptions">
                <PermissionCheckbox
                  checked={
                    this.state.own_all &&
                    (this.state.create_content || this.state.publish_content)
                  }
                  disabled={
                    !this.state.own_all ||
                    (this.state.own_all && this.state.publish_content)
                  }
                  name="create_content"
                  onChange={e => {
                    this.handleCreateContentChange(e);
                  }}
                >
                  {t('settings.tab.roles.edit.create_content_label', {
                    contentTypes: this.contentTypesText(),
                  })}
                </PermissionCheckbox>
                <PermissionCheckbox
                  checked={this.state.own_all && this.state.publish_content}
                  disabled={!this.state.own_all}
                  name="publish_content"
                  onChange={e => {
                    this.handlePublishContentChange(e);
                  }}
                >
                  {t('settings.tab.roles.edit.publish_content_label')}
                </PermissionCheckbox>
              </div>
            </RadioFieldGroup>
          </div>
          <EventManagement
            disabled={!this.props.role.editable || this.state.loading}
            selectedValue={this.eventManagementState()}
            onChange={e => {
              this.handleEventManagementRadioChange(e);
            }}
          />
          <FeatureFlag features="skills">
            <SkillsManagement
              disabled={!this.props.role.editable || this.state.loading}
              selectedValue={this.skillsManagementState()}
              onChange={e => {
                this.handleSkillsManagementRadioChange(e);
              }}
            />
          </FeatureFlag>

          <div className="EditRole-section">
            <h3>{t('settings.tab.roles.edit.program_management.title')}</h3>
            <PermissionCheckbox
              checked={this.state.manage_company_settings}
              name="manage_company_settings"
            >
              {t('settings.tab.roles.edit.manage_company_settings_label')}
            </PermissionCheckbox>
            <PermissionCheckbox
              checked={this.state.create_edit_tags}
              name="create_edit_tags"
            >
              {t('settings.tab.roles.edit.create_edit_tags_label')}
            </PermissionCheckbox>
            <PermissionCheckbox
              checked={this.state.create_edit_triggers}
              name="create_edit_triggers"
            >
              {t('settings.tab.roles.edit.create_edit_triggers_label')}
            </PermissionCheckbox>
            {(hasChromeExtension || hasWebWidget) && (
              <PermissionCheckbox
                checked={this.state.chrome_extension_mappings}
                disabled={
                  !this.state.own_all ||
                  !this.state.edit_all ||
                  !this.state.create_content
                }
                name="chrome_extension_mappings"
              >
                <RelatedUrlLabel {...this.props.features} />
                <InfoTooltip
                  tooltip={t('settings.tab.roles.edit.related_url_tooltip')}
                  className="ml-4"
                />
              </PermissionCheckbox>
            )}
            <FeatureFlag features={['readiness-enable-lesson-templates']}>
              <PermissionCheckbox
                checked={this.state.create_edit_templates}
                name="create_edit_templates"
              >
                {t('settings.tab.roles.edit.create_edit_lesson_templates')}
              </PermissionCheckbox>
            </FeatureFlag>
            <PermissionCheckbox
              checked={this.state.manage_content_library}
              name="manage_content_library"
            >
              <ContentLibraryLabel {...this.props.features} />
            </PermissionCheckbox>
            {this.props.role.default_role_mapping === ADMIN && (
              <PermissionCheckbox
                checked={this.state.create_edit_roles}
                name="create_edit_roles"
              >
                {t('settings.tab.roles.edit.create_edit_roles_label')}
              </PermissionCheckbox>
            )}
          </div>

          <div className="ButtonGroup jc-sb mt-32">
            <div>
              {this.deletable() && (
                <Button
                  className="delete"
                  data-toggle={this.deleteButtonDisabled() ? 'tooltip' : null}
                  data-original-title={this.deleteButtonTooltipText()}
                  onClick={() => this.setState({ showDeleteModal: true })}
                  disabled={this.deleteButtonDisabled()}
                >
                  {t('settings.tab.roles.delete.delete_role')}
                </Button>
              )}
            </div>
            <div>
              {this.props.role.editable && (
                <LinkButton
                  type="primary"
                  className="cancel mr-24 ml-24"
                  onClick={this.props.onCancel}
                >
                  {t('common.cancel')}
                </LinkButton>
              )}
              <Button
                onClick={
                  this.props.role.editable
                    ? this.handleSubmit
                    : this.props.onCancel
                }
                disabled={this.nameBlank() || this.state.loading}
                customColor
                id="editRoleSubmitButton"
              >
                {this.submitButtonText()}
              </Button>
            </div>
          </div>
        </div>
      </Loadable>
    );
  }
}

export default EditRole;
